/**
 * MX block scaling, spelled `.weight` + `.scale` — the third spelling of MXFP4.
 *
 * ## The failure this exists to fix
 *
 * `deepseek-ai/DeepSeek-V4-Flash` stores every routed expert projection as a
 * pair:
 *
 *     layers.10.ffn.experts.0.w1.weight   I8       [2048, 2048]   4 194 304 B
 *     layers.10.ffn.experts.0.w1.scale    F8_E8M0  [2048,  128]     262 144 B
 *
 * `.scale` is not in `PART_SUFFIXES` — only `.scales` is — so the two never even
 * grouped. The payload stood alone reporting its **byte** count as parameters at
 * a nominal 8 bits each, and the biggest rows on that model's map were 2x wrong
 * in the denominator and about 1.9x wrong in the headline. Reported externally
 * on a trending model, by the same reader who diagnosed the AWQ/GPTQ ordering.
 *
 * It is the Kimi-K3 bug one spelling over: stored bytes counted as weights.
 *
 * ## Why no config keying could have caught it
 *
 * This is MXFP4's *third* naming in the corpus and the first that the repo does
 * not declare where a reader would look:
 *
 * | repo | payload / scale | `quantization_config` says |
 * | --- | --- | --- |
 * | `openai/gpt-oss-20b` | `_blocks` / `_scales`, both U8 | `quant_method: "mxfp4"` |
 * | `moonshotai/Kimi-K3` | `weight_packed` / `weight_scale`, both U8 | `compressed-tensors` + `format: mxfp4-pack-quantized` |
 * | `deepseek-ai/DeepSeek-V4-Flash` | `.weight` / `.scale`, I8 + F8_E8M0 | `quant_method: "fp8"` |
 *
 * That last declaration is *true* — of the attention tensors, which really are
 * fp8 with 128x128 blocks — and silent about the experts, which are 88.8% of the
 * checkpoint's bytes. A repo can under-declare its own second scheme, so the
 * detection has to be by construction. Same lesson as vocabulary-versus-
 * architecture, applied to quantization formats: **detect by construction, not
 * by spelling.**
 *
 * (The repo does say `expert_dtype: "fp4"` at the config root, outside
 * `quantization_config`, and it agrees with what the shapes work out to. It is
 * corroboration, recorded in `docs/evidence/`, and deliberately not consulted
 * here: a vendor-specific key would find this model and no other, and reading it
 * would invite the mistake of trusting it when it disagreed.)
 *
 * ## What makes the pair checkable rather than assumed
 *
 * `block-scaled.ts` explains at length why shapes alone cannot separate
 * 4-bit/block-32 from 8-bit/block-16: one equation constrains only the product,
 * and picking wrong scales the headline by exactly two. That objection applies
 * here in full, and what answers it is a **conjunction of three gates**, no one
 * of which is sufficient.
 *
 * This comment previously claimed the scale dtype alone was the answer:
 * "`F8_E8M0` exists for one purpose … nothing else uses it". That was wrong,
 * and the table below this one refutes it — the 375 attention pairs in this very
 * repo are E8M0 scales on a *non-MX* scheme, and the config says so in as many
 * words: `"scale_fmt": "ue8m0"` under `quant_method: "fp8"`. The sentence was
 * defending a correct decision, which is exactly where a wrong reason survives
 * longest (reviewer, 2026-08-09).
 *
 * So, stated accurately. `F8_E8M0` is an exponent-only 8-bit float defined by
 * the OCP Microscaling (MX) specification, where the block is fixed at **32
 * elements**. Its presence *narrows* the possibilities — an ordinary int8
 * quantizer writes F32 or F16 per-channel scales — but it does not identify MX
 * on its own. What identifies MX block scaling is all three of:
 *
 *   1. an **integer payload dtype** (`I8`/`U8`), which the fp8 pairs fail;
 *   2. an **E8M0 sidecar**, which an ordinary int8 checkpoint fails;
 *   3. **MX-consistent geometry** — identical leading dims, and a last-axis
 *      ratio that yields an exact width — which a 2-D block grid fails.
 *
 * The geometry is a co-equal gate, not a consequence of the other two. Only
 * once all three hold is the block known to be 32, and only then does the
 * payload's byte count say the width:
 *
 *     logical last axis  =  32 x scale_last
 *     bits               =  payload bytes x 8 / (leading x logical last)
 *
 *     2048 x 128 scales -> 4096 logical    4 194 304 x 8 / (2048 x 4096) = 4.00
 *     effective, scales included:      (4 194 304 + 262 144) x 8 / that = 4.25
 *
 * 4.25 is the same constant `gpt-oss` lands on, which is what MXFP4 costs: four
 * bits of payload plus one E8M0 byte per 32 values. `bits` coming out at exactly
 * 4.00 rather than near it is the check with teeth — a pair whose geometry says
 * 4.03 is some other layout wearing these suffixes, and is refused.
 *
 * ## Why it cannot fire on the fp8 attention beside it
 *
 * The same repo's attention tensors are also `<name>.weight` + `<name>.scale`
 * with an E8M0 scale plane, and they must not fold: they are 1 byte per
 * parameter already. Measured over every payload/scale pair in the repo, the two
 * populations do not overlap on either test:
 *
 *     33 792 pairs   I8      + F8_E8M0   leading dims same     last-dim ratio 16
 *        375 pairs   F8_E4M3 + F8_E8M0   leading dims DIFFER   last-dim ratio 128
 *
 * The payload dtype gate excludes the fp8 pairs outright; the shape tests
 * exclude them twice over. `wq_a.scale [8, 32]` against `wq_a [1024, 4096]` is
 * 1024/128 by 4096/128 — a two-dimensional block grid, not a run along the last
 * axis — so it fails "leading dims identical" before any arithmetic runs.
 *
 * A `router.scale` or any other stray sidecar fails on dtype, on rank, or on the
 * exact-width test. Three independent gates, and the arithmetic has to land on a
 * power-of-two width exactly.
 *
 * ## What is *not* done here
 *
 * Decoding. This is the accounting fix, and it needs no payload bytes at all —
 * only the header. Reading actual MXFP4 values back is a separate step and stays
 * gated on ground truth, per the standing rule that a decoder ships only when it
 * has been measured against a reference implementation.
 *
 * Pure: shapes in, arithmetic out, no I/O.
 */

export interface E8m0TensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  readonly byteLength: number;
}

export interface E8m0ScaledGroup {
  /** Logical name — the payload's own name, e.g. `...experts.0.w1.weight`. */
  readonly prefix: string;
  /** The payload tensor. */
  readonly payloadName: string;
  /** Both physical tensors, payload first. */
  readonly partNames: readonly string[];
  /** Recovered element width. 4 for MXFP4; 8 for an MX int8/fp8 payload. */
  readonly bits: number;
  /** 32, by the MX specification. Carried so the label reads like the others. */
  readonly groupSize: number;
  /** Weights in the logical matrix, before packing. */
  readonly params: number;
  /** Payload plus scales. */
  readonly bytes: number;
  /** What the scheme actually costs, scales included. */
  readonly effectiveBpw: number;
  /** The payload alone. Exactly `bits`, by construction — see `payloadBpw`. */
  readonly payloadBpw: number;
}

/**
 * The scale plane's dtype — one of the three gates, not the whole of the case.
 *
 * E8M0 is MX's block-scale format, but DeepSeek's *fp8* scheme also writes E8M0
 * scales (`"scale_fmt": "ue8m0"`) on a 128x128 grid, in this same repo. So this
 * narrows and does not decide; the geometry checks below carry equal weight.
 */
const MX_SCALE_DTYPE = "F8_E8M0";

/**
 * MX's block size, in logical elements. Fixed by the specification — and only
 * applicable once the payload dtype *and* the geometry have agreed this is MX.
 */
const MX_BLOCK = 32;

/** The payload plane is a byte array; anything wider is not this layout. */
const BYTE_DTYPES: ReadonlySet<string> = new Set(["U8", "I8"]);

const SCALE_SUFFIXES = [".scale", ".scales"] as const;

function product(shape: readonly number[]): number {
  return shape.reduce((a, b) => a * b, 1);
}

/**
 * Widths a byte plane can hold without a remainder.
 *
 * MXFP6 is a real MX format and is deliberately absent: six bits per value packs
 * four values into three bytes, which this arithmetic does not describe. Such a
 * pair is refused rather than approximated.
 */
function isExactWidth(bits: number): boolean {
  return Number.isInteger(bits) && bits > 0 && bits <= 8 && 8 % bits === 0;
}

function verifyPair(
  payload: E8m0TensorLike,
  scale: E8m0TensorLike,
): Omit<E8m0ScaledGroup, "prefix"> | undefined {
  if (!BYTE_DTYPES.has(payload.dtype)) return undefined;
  if (scale.dtype !== MX_SCALE_DTYPE) return undefined;

  // Both planes must be dense arrays of their own shape, or the shapes describe
  // something the byte counts contradict.
  if (payload.byteLength !== product(payload.shape)) return undefined;
  if (scale.byteLength !== product(scale.shape)) return undefined;

  /*
   * Scales run along the last axis, one per block of 32. So every axis but the
   * last has to match exactly — this is the test the fp8 attention pairs fail,
   * where the scale plane is a 2-D grid over a 128x128 block instead.
   */
  if (payload.shape.length < 2) return undefined;
  if (payload.shape.length !== scale.shape.length) return undefined;
  for (let i = 0; i < payload.shape.length - 1; i++) {
    if (payload.shape[i] !== scale.shape[i]) return undefined;
  }

  const scaleLast = scale.shape.at(-1) ?? 0;
  const payloadLast = payload.shape.at(-1) ?? 0;
  if (scaleLast <= 0 || payloadLast <= 0) return undefined;

  const logicalLast = scaleLast * MX_BLOCK;
  const leading = product(payload.shape.slice(0, -1));
  const params = leading * logicalLast;
  if (params <= 0) return undefined;

  /*
   * The width, recovered rather than assumed — and required to be exact.
   *
   * With the block pinned at 32 by the scale dtype, this is fully determined:
   * a payload holding 4096 logical values in 2048 bytes is 4-bit, and one
   * holding 4096 in 4096 is 8-bit. A ratio that lands between them is not this
   * layout, and is refused rather than rounded to the nearest plausible width.
   */
  const bits = (payload.byteLength * 8) / params;
  if (!isExactWidth(bits)) return undefined;

  const bytes = payload.byteLength + scale.byteLength;
  return {
    payloadName: payload.name,
    partNames: [payload.name, scale.name],
    bits,
    groupSize: MX_BLOCK,
    params,
    bytes,
    effectiveBpw: (bytes * 8) / params,
    payloadBpw: bits,
  };
}

/**
 * Every `<name>.weight` + `<name>.scale` pair whose geometry verifies as MX.
 *
 * Takes no `declared`, unlike its siblings, and that absence is the point. The
 * other folders need a config because shapes alone leave the width ambiguous;
 * here the scale plane's dtype resolves it from the file. Accepting a config
 * would also mean accepting `DeepSeek-V4-Flash`'s, which says `fp8` — true of
 * its attention, wrong about these tensors, and it would veto every correct
 * fold in the repo.
 */
export function findE8m0ScaledGroups(
  tensors: readonly E8m0TensorLike[],
): E8m0ScaledGroup[] {
  const byName = new Map(tensors.map((t) => [t.name, t]));
  const groups: E8m0ScaledGroup[] = [];

  for (const payload of tensors) {
    if (!payload.name.endsWith(".weight")) continue;
    const stem = payload.name.slice(0, -".weight".length);

    for (const suffix of SCALE_SUFFIXES) {
      const scale = byName.get(`${stem}${suffix}`);
      if (!scale) continue;
      const verified = verifyPair(payload, scale);
      if (!verified) continue;

      groups.push({ prefix: payload.name, ...verified });
      break;
    }
  }
  return groups;
}
